import React, { useState, useRef } from 'react';
import { Upload, X, File, CheckCircle, AlertCircle, FileCode } from 'lucide-react';

const FileUpload = ({ 
    label,
    accept = "*/*",
    maxSize = 5 * 1024 * 1024, // 5MB default
    multiple = false,
    onFileSelect,
    required = false,
    error,
    helperText,
    className = ""
}) => {
    const [dragActive, setDragActive] = useState(false);
    const [files, setFiles] = useState([]);
    const [uploadError, setUploadError] = useState(null);
    const inputRef = useRef(null);

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    };

    const validateFile = (file) => {
        // Check file size
        if (file.size > maxSize) {
            return `File size exceeds ${formatFileSize(maxSize)}`;
        }

        // Check file type if accept is specified
        if (accept !== "*/*") {
            const acceptedTypes = accept.split(',').map(type => type.trim());
            const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
            const fileMimeType = file.type;
            
            const isAccepted = acceptedTypes.some(type => {
                if (type.startsWith('.')) {
                    return fileExtension === type.toLowerCase();
                }
                return fileMimeType.match(new RegExp(type.replace('*', '.*')));
            });

            if (!isAccepted) {
                return `File type not accepted.`;
            }
        }

        return null;
    };

    const handleFiles = (fileList) => {
        setUploadError(null);
        const newFiles = multiple ? Array.from(fileList) : [fileList[0]];

        // Validate all files
        for (const file of newFiles) {
            const error = validateFile(file);
            if (error) {
                setUploadError(error);
                return;
            }
        }

        // Update files list
        const updatedFiles = multiple ? [...files, ...newFiles] : newFiles;        
        setFiles(updatedFiles);

        // Call parent callback
        if (onFileSelect) {
            onFileSelect(multiple ? updatedFiles : updatedFiles[0]);
        }
    };

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            handleFiles(e.dataTransfer.files);
        }
    };

    const handleChange = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            handleFiles(e.target.files);
        }
    };

    const removeFile = (index) => {
        const updatedFiles = files.filter((_, i) => i !== index);
        setFiles(updatedFiles);
        
        if (onFileSelect) {
            onFileSelect(multiple ? updatedFiles : null);
        }

        // Reset input
        if (inputRef.current) {
            inputRef.current.value = '';
        }
    };

    const onButtonClick = () => {
        inputRef.current?.click();
    };

    const displayError = error || uploadError;

    return (
        <div className={`flex flex-col w-full bg-background-light ${className}`}>
            {label && (
                <label className="text-xs text-primary-dark mb-1 h-4 gap-1 flex items-center">
                    <File className="w-4 h-4" />
                    {label}
                    {required && <span className="text-red-500">*</span>}
                    {displayError && (
                        <span className="text-red-600 ml-auto flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" />
                            {displayError}
                        </span>
                    )}
                </label>
            )}

            {!files || files.length === 0 && (
            <div
                className={`
                    relative border-2 border-dashed rounded-lg p-6 text-center
                    transition-all duration-200 cursor-pointer
                    ${dragActive 
                        ? 'border-accent-main bg-background-light/30' 
                        : displayError
                            ? 'border-red-500'
                            : 'border-accent-main hover:bg-primary-light/20'
                    }
                `}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={onButtonClick}
            >
                <input
                    ref={inputRef}
                    type="file"
                    className="hidden"
                    multiple={multiple}
                    accept={accept}
                    onChange={handleChange}
                    required={required}
                />

                <div className="flex flex-col items-center gap-3">
                    <div className={`
                        p-3 rounded-full 
                        ${dragActive 
                            ? 'bg-accent-main text-white' 
                            : 'bg-primary-light text-accent-main'
                        }
                    `}>
                        <Upload className="w-6 h-6" />
                    </div>

                    <div>
                        <p className="text-primary-dark font-semibold mb-1">
                            {dragActive 
                                ? 'Drop files here' 
                                : 'Click to upload or drag and drop'
                            }
                        </p>
                        <p className="text-sm text-text-muted text-center justify-center flex">
                            {accept !== "*/*" && `${accept}`}
                        </p>
                        <p className="text-sm text-text-muted overflow-wrap break-words">
                            {maxSize && ` • Max size: ${formatFileSize(maxSize)}`}
                        </p>
                    </div>
                </div>
            </div>
            )}

            {helperText && !displayError && (
                <p className="text-xs text-text-muted mt-1">{helperText}</p>
            )}

            {/* File List */}
            {files.length > 0 && (
                <div className="mt-3 space-y-2">
                    {files.map((file, index) => (
                        <div
                            key={index}
                            className="flex items-center gap-3 p-3 bg-primary-light border border-accent-main rounded-lg min-w-0"
                        >
                            <div className="flex-shrink-0">
                                <File className="w-5 h-5 text-accent-main" />
                            </div>
                            
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-primary-dark break-words" title={file.name}>
                                    {file.name}
                                </p>
                                <p className="text-xs text-text-muted">
                                    {formatFileSize(file.size)}
                                </p>
                            </div>

                            <div className="flex items-center gap-2 flex-shrink-0">
                                <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        removeFile(index);
                                    }}
                                    className="p-1 hover:bg-red-100 rounded transition-colors flex-shrink-0"
                                    aria-label="Remove file"
                                >
                                    <X className="w-4 h-4 text-red-600" />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default FileUpload;
